import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Settings,
  Shield,
  User as UserIcon,
  LogOut,
  ChevronDown,
  Building2,
  CreditCard,
  Sparkles,
  RefreshCw,
  DoorOpen,
  Search,
} from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import HelpDropdown from "@/components/HelpDropdown";
import KbCommandPalette from "@/components/KbCommandPalette";
import BuildChip from "@/components/BuildChip";
import { useAuth } from "@/contexts/AuthContext";

/**
 * v1.2 — Top-right header cluster for the authenticated app.
 * Left → right: BuildChip (super_admin only) · Help · Bell · Settings gear · Avatar dropdown.
 *
 * v1.2 PR-C.2 — When rendered inside the super-admin chrome
 * (`context="super_admin"`), the "SUPER ADMIN" pill is followed by an
 * always-visible **"Exit super admin"** button so the user can return to
 * the standard app shell from any super-admin page. Mirrors the avatar
 * dropdown's "Super admin workspace" entry (org chrome) and contractually
 * does NOT end an active impersonation — operators frequently want to
 * impersonate from /super-admin and then leave the workspace to navigate
 * operational pages as the impersonated org.
 */
export default function Header({ context = "org" }) {
  const { user, logout, endImpersonation, impersonating } = useAuth();
  const isSuper = user?.role === "super_admin";
  const isSuperContext = context === "super_admin";
  const navigate = useNavigate();
  const [cmdkOpen, setCmdkOpen] = useState(false);

  // Phase B3.1 — global Cmd-K / Ctrl-K shortcut.
  useEffect(() => {
    const onKey = (e) => {
      // Mac uses metaKey (Cmd), Windows/Linux use ctrlKey.
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setCmdkOpen((v) => !v);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return (
    <header
      className="sticky top-0 z-40 h-14 bg-white dark:bg-navy-800 border-b border-navy-100 dark:border-navy-700 flex items-center justify-between px-4 md:px-6"
      data-testid="app-header"
    >
      {/* Left spacer / context badge */}
      <div className="flex items-center gap-3">
        {isSuperContext && (
          <>
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#FF8A4C]/10 text-[#FF8A4C] text-[11px] font-semibold tracking-wider uppercase"
              data-testid="super-admin-pill"
            >
              <Shield className="w-3.5 h-3.5" /> Super admin
            </span>
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-navy-200 dark:border-navy-700 bg-white dark:bg-navy-800 text-[11px] font-semibold tracking-wider uppercase text-navy-700 dark:text-navy-200 hover:border-orange-300 hover:text-orange-600 transition-colors"
              data-testid="header-exit-super-admin"
              title="Leave the super-admin workspace (any active impersonation continues)."
            >
              <DoorOpen className="w-3.5 h-3.5" /> Exit super admin
            </button>
          </>
        )}
        {impersonating && !isSuperContext && (
          <button
            onClick={endImpersonation}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-warning/15 text-warning-700 text-[11px] font-semibold uppercase tracking-wider hover:bg-warning/25"
            data-testid="header-end-impersonation"
            title="End impersonation"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Impersonating
          </button>
        )}
      </div>

      {/* Right cluster — 8px gap between items, 12px before avatar */}
      <div className="flex items-center gap-2">
        {isSuper && <BuildChip />}
        {/* Phase B3.1 — Cmd-K KB search palette trigger. Slim search box on
            desktop (≥md), Search icon button on mobile. Same component
            handles the global Cmd-K / Ctrl-K shortcut listener too. */}
        <button
          type="button"
          onClick={() => setCmdkOpen(true)}
          aria-label="Search the knowledge base"
          className="hidden md:inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-navy-200 dark:border-navy-700 bg-white dark:bg-navy-800 text-navy-500 dark:text-navy-300 hover:border-orange-300 hover:text-navy-900 dark:hover:text-white transition-colors text-xs"
          data-testid="kb-cmd-k-trigger"
        >
          <Search className="w-3.5 h-3.5" />
          <span>Search KB</span>
          <kbd className="hidden lg:inline ml-2 px-1.5 py-0.5 rounded bg-navy-100 dark:bg-navy-700 text-[10px] font-mono text-navy-500 dark:text-navy-300">
            ⌘K
          </kbd>
        </button>
        <button
          type="button"
          onClick={() => setCmdkOpen(true)}
          aria-label="Search the knowledge base"
          className="md:hidden w-9 h-9 rounded-full flex items-center justify-center text-navy-500 dark:text-navy-300 hover:bg-navy-50 dark:hover:bg-navy-700 hover:text-navy-900 dark:hover:text-white transition-colors"
          data-testid="kb-cmd-k-trigger-mobile"
        >
          <Search className="w-4 h-4" />
        </button>
        <KbCommandPalette open={cmdkOpen} onOpenChange={setCmdkOpen} />
        {/* Phase B3 — replaces the previous static HelpCircle link to entrybook.site.
            The dropdown gives every user direct access to KB + tickets and
            adds a super-admin section for the cross-org ticket console. */}
        <HelpDropdown />
        <NotificationBell />
        <button
          type="button"
          aria-label="Settings"
          onClick={() => navigate("/settings")}
          className="w-9 h-9 rounded-full flex items-center justify-center text-navy-500 dark:text-navy-300 hover:bg-navy-50 dark:hover:bg-navy-700 hover:text-navy-900 dark:hover:text-white transition-colors"
          data-testid="header-settings-gear"
        >
          <Settings className="w-5 h-5" />
        </button>
        <div className="w-3" />
        <AvatarMenu user={user} isSuper={isSuper} isSuperContext={isSuperContext} logout={logout} navigate={navigate} />
      </div>
    </header>
  );
}

function AvatarMenu({ user, isSuper, isSuperContext, logout, navigate }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const initials = (user?.name || user?.email || "?")
    .split(/[\s.@]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");

  const item = "w-full text-left px-4 py-2.5 text-sm text-navy-800 dark:text-navy-100 hover:bg-navy-50 dark:hover:bg-navy-700 inline-flex items-center gap-2.5";

  const onClick = (path) => () => { setOpen(false); navigate(path); };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 pl-1 pr-2 py-1 rounded-full hover:bg-navy-50 dark:hover:bg-navy-700 transition-colors"
        data-testid="header-avatar-btn"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="w-8 h-8 rounded-full bg-navy-900 text-white text-xs font-semibold flex items-center justify-center">
          {initials || <UserIcon className="w-4 h-4" />}
        </span>
        <ChevronDown className="w-3.5 h-3.5 text-navy-500 dark:text-navy-300" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 w-[280px] bg-white dark:bg-navy-800 border border-navy-100 dark:border-navy-700 rounded-xl shadow-xl py-2 z-50"
          data-testid="header-avatar-menu"
        >
          <div className="px-4 py-3 border-b border-navy-100 dark:border-navy-700">
            <div className="text-xs text-navy-500 dark:text-navy-300">Signed in as</div>
            <div className="text-sm font-semibold text-navy-900 dark:text-white truncate">{user?.name || user?.email}</div>
            <div className="text-[11px] text-orange-600 uppercase tracking-wide font-medium mt-0.5">
              {user?.role?.replace("_", " ")}
            </div>
          </div>
          <button className={item} onClick={onClick("/account/profile")} data-testid="avatar-menu-profile">
            <UserIcon className="w-4 h-4 text-navy-500 dark:text-navy-300" /> My profile
          </button>
          {user?.org_id && (
            <button className={item} onClick={onClick("/settings")} data-testid="avatar-menu-organisation">
              <Building2 className="w-4 h-4 text-navy-500 dark:text-navy-300" /> My organisation
            </button>
          )}
          {user?.org_id && (
            <button className={item} onClick={onClick("/billing")} data-testid="avatar-menu-billing">
              <CreditCard className="w-4 h-4 text-navy-500 dark:text-navy-300" /> Billing
            </button>
          )}
          <button className={item} onClick={onClick("/onboarding")} data-testid="avatar-menu-setup">
            <Sparkles className="w-4 h-4 text-navy-500 dark:text-navy-300" /> Setup checklist
          </button>

          {isSuper && !isSuperContext && (
            <>
              <div className="h-px bg-navy-100 dark:bg-navy-700 my-1" />
              <button className={item} onClick={onClick("/super-admin")} data-testid="avatar-menu-super-admin">
                <Shield className="w-4 h-4 text-[#FF8A4C]" /> Super admin workspace
              </button>
            </>
          )}
          {isSuper && isSuperContext && (
            <>
              <div className="h-px bg-navy-100 dark:bg-navy-700 my-1" />
              <button className={item} onClick={onClick("/dashboard")} data-testid="avatar-menu-exit-super-admin">
                <DoorOpen className="w-4 h-4 text-[#FF8A4C]" /> Exit super admin
              </button>
            </>
          )}

          <div className="h-px bg-navy-100 dark:bg-navy-700 my-1" />
          <button
            className="w-full text-left px-4 py-2.5 text-sm text-danger hover:bg-danger/5 dark:hover:bg-danger/15 inline-flex items-center gap-2.5"
            onClick={() => { setOpen(false); logout(); }}
            data-testid="avatar-menu-logout"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}
